import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import Navbar from '../components/shared/Navbar';
import DashboardSidebar from '../components/shared/DashboardSidebar';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  ShoppingCart, Package, Truck, CheckCircle, Clock, XCircle,
  Search, Filter, Eye, MessageSquare, DollarSign, MapPin, Calendar,
  ArrowUpRight, ArrowDownLeft, Loader2
} from 'lucide-react';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  processing: { label: 'Processing', color: 'bg-purple-100 text-purple-700', icon: Package },
  shipped: { label: 'Shipped', color: 'bg-indigo-100 text-indigo-700', icon: Truck },
  delivered: { label: 'Delivered', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700', icon: XCircle }
};

const paymentStatusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  partial: { label: 'Partial', color: 'bg-blue-100 text-blue-700' },
  refunded: { label: 'Refunded', color: 'bg-gray-100 text-gray-700' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700' }
};

const OrderManagementPage = () => {
  const navigate = useNavigate();
  const location = window.location.pathname;
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  // Default viewMode based on route - buyer routes show buyer view
  const [viewMode, setViewMode] = useState(location.includes('/buyer') ? 'buyer' : 'seller');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      const token = localStorage.getItem('atff_token');
      await axios.put(`${API}/orders/${orderId}/status`, 
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      toast.success(`Order status updated to ${newStatus}`);
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const filteredOrders = orders.filter(order => {
    // Filter by role (buyer or seller)
    const roleMatch = viewMode === 'seller' 
      ? order.seller_id === user?.user_id 
      : order.buyer_id === user?.user_id;
    
    // Filter by status
    const statusMatch = statusFilter === 'all' || order.status === statusFilter;
    
    // Filter by search
    const searchMatch = !searchTerm || 
      order.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.order_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return roleMatch && statusMatch && searchMatch;
  });

  const sellerOrders = orders.filter(o => o.seller_id === user?.user_id);
  const buyerOrders = orders.filter(o => o.buyer_id === user?.user_id);

  const getStatusActions = (order) => {
    if (viewMode !== 'seller') return null;
    
    const actions = {
      pending: ['confirmed', 'cancelled'],
      confirmed: ['processing', 'cancelled'],
      processing: ['shipped'],
      shipped: ['delivered'],
      delivered: ['completed']
    };
    
    return actions[order.status] || [];
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex">
          <DashboardSidebar />
          <div className="flex-1 flex items-center justify-center h-[calc(100vh-80px)]">
            <Loader2 className="w-8 h-8 animate-spin text-continental" />
          </div>
        </div>
      </div>
    );
  }

  // Determine base path for order detail links
  const orderDetailBasePath = viewMode === 'buyer' ? '/buyer/orders' : '/orders';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="flex">
        <DashboardSidebar />
        
        <main className="flex-1 p-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-charcoal">Order Management</h1>
            <p className="text-gray-600">Track and manage your orders</p>
          </div>
          
          {/* View Toggle */}
          <div className="flex items-center gap-2 bg-gray-100 p-1 rounded-lg">
            <Button
              variant={viewMode === 'seller' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('seller')}
              className={viewMode === 'seller' ? 'bg-continental' : ''}
            >
              <ArrowDownLeft className="w-4 h-4 mr-1" />
              Sales ({sellerOrders.length})
            </Button>
            <Button
              variant={viewMode === 'buyer' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('buyer')}
              className={viewMode === 'buyer' ? 'bg-blue-600' : ''}
            >
              <ArrowUpRight className="w-4 h-4 mr-1" />
              Purchases ({buyerOrders.length})
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Orders</p>
                  <p className="text-2xl font-bold">{filteredOrders.length}</p>
                </div>
                <ShoppingCart className="w-8 h-8 text-gray-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {filteredOrders.filter(o => o.status === 'pending').length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-yellow-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {filteredOrders.filter(o => ['confirmed', 'processing', 'shipped'].includes(o.status)).length}
                  </p>
                </div>
                <Truck className="w-8 h-8 text-blue-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">
                    {filteredOrders.filter(o => ['delivered', 'completed'].includes(o.status)).length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-300" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search by product name or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Orders List */}
        {filteredOrders.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ShoppingCart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No orders found</h3>
              <p className="text-gray-500">
                {viewMode === 'seller' 
                  ? "You haven't received any orders yet"
                  : "You haven't placed any orders yet"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map(order => {
              const status = statusConfig[order.status] || statusConfig.pending;
              const paymentStatus = paymentStatusConfig[order.payment_status] || paymentStatusConfig.pending;
              const StatusIcon = status.icon;
              const availableActions = getStatusActions(order);

              return (
                <Card key={order.order_id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex flex-col lg:flex-row lg:items-center gap-4">
                      {/* Order Info */}
                      <div className="flex-1">
                        <div className="flex items-start gap-3">
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Package className="w-8 h-8 text-gray-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-charcoal truncate">{order.product_name}</h3>
                              <Badge className={status.color}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {status.label}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-500 mb-2">
                              Order ID: {order.order_id}
                            </p>
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              <span className="flex items-center gap-1">
                                <Package className="w-4 h-4" />
                                {order.quantity} units
                              </span>
                              <span className="flex items-center gap-1">
                                <DollarSign className="w-4 h-4" />
                                {order.currency} {order.total_amount?.toLocaleString()}
                              </span>
                              <span className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {order.delivery_country}
                              </span>
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {new Date(order.created_at).toLocaleDateString()}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Payment Status & Actions */}
                      <div className="flex flex-col items-end gap-2">
                        <Badge className={paymentStatus.color}>
                          Payment: {paymentStatus.label}
                        </Badge>
                        
                        <div className="flex gap-2">
                          {viewMode === 'seller' && availableActions?.length > 0 && (
                            <Select onValueChange={(newStatus) => updateOrderStatus(order.order_id, newStatus)}>
                              <SelectTrigger className="w-36 h-9">
                                <SelectValue placeholder="Update Status" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableActions.map(action => (
                                  <SelectItem key={action} value={action}>
                                    {statusConfig[action]?.label || action}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`${orderDetailBasePath}/${order.order_id}`}>
                              <Eye className="w-4 h-4 mr-1" />
                              Details
                            </Link>
                          </Button>
                          <Button variant="outline" size="sm">
                            <MessageSquare className="w-4 h-4 mr-1" />
                            Contact
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        </main>
      </div>
    </div>
  );
};

export default OrderManagementPage;
